import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type {
  ApiFallo, AudioReproduccion, ClaseFuente, FuenteReproduccion, SubtituloReproduccion,
} from '../lib/api.js';
import { formatearDuracion, formatearTamano } from '../lib/eventos.js';
import { nombreIdioma } from '../lib/idiomas.js';
import { mimeDe, PistaSubtitulos, SesionReproduccion, urlStream, urlSubtitulo } from '../lib/reproductor.js';

/** El reproductor integrado (D-26): ver la película en el navegador ANTES de decidir qué
 *  hacer con ella —qué audio trae de verdad, si el subtítulo forzado es el que dice ser,
 *  cómo quedó la conversión— con las mismas pistas que se editan en Conversiones → Pistas.
 *
 *  No toca nada: no escribe en disco, no cambia estados ni pasa por la cola de conversión.
 *  Lo que el navegador no descodifica lo recodifica ffmpeg al vuelo, y la pantalla dice
 *  qué se está copiando y qué no, porque cambia lo que se ve: una vista previa a 720p no es
 *  la calidad del fichero. */

function describirClase(c: ClaseFuente): { texto: string; nota: string } {
  switch (c) {
    case 'original':
      return { texto: 'Original', nota: 'El fichero tal como lo bajó aMule, con todas sus pistas.' };
    case 'convertida':
      return { texto: 'Convertida', nota: 'El MP4 que espera en la carpeta de conversiones: lo que se colocaría.' };
    case 'colocada':
      return { texto: 'En la biblioteca', nota: 'La copia que ya está en la biblioteca de Plex.' };
  }
}

function rotuloAudio(a: AudioReproduccion): string {
  const partes = [nombreIdioma(a.idioma), a.codec.toUpperCase()];
  if (a.canales) partes.push(a.canales === 2 ? 'estéreo' : a.canales === 1 ? 'mono' : `${a.canales} canales`);
  if (a.titulo) partes.push(`«${a.titulo}»`);
  return partes.join(' · ');
}

function rotuloSubtitulo(s: SubtituloReproduccion): string {
  const partes = [nombreIdioma(s.idioma)];
  if (s.forzado) partes.push('forzado');
  if (s.titulo) partes.push(`«${s.titulo}»`);
  partes.push(s.entrega === 'incrustar' ? `de imagen (${s.codec})` : s.codec);
  return partes.join(' · ');
}

/** Con qué pistas arrancar. Audio: la marcada por defecto, o la primera. Subtítulos:
 *  ninguno, que es lo que hace un reproductor cualquiera; lo que se quiere mirar se elige. */
function seleccionInicial(f: FuenteReproduccion): { audio: number | null } {
  const porDefecto = f.audio.find((a) => a.porDefecto) ?? f.audio[0];
  return { audio: porDefecto?.indice ?? null };
}

export function Reproductor({
  downloadId,
  titulo,
  abierto,
  onCerrar,
}: {
  downloadId: string;
  titulo: string;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const info = useQuery({
    queryKey: ['reproduccion', downloadId],
    queryFn: () => api.reproduccion(downloadId),
    enabled: abierto,
    staleTime: 60_000,
    // Volver a la pestaña no tiene por qué reabrir el vídeo desde cero.
    refetchOnWindowFocus: false,
  });

  const [clase, setClase] = useState<ClaseFuente | null>(null);
  const [audio, setAudio] = useState<number | null>(null);
  const [subtitulo, setSubtitulo] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** El vídeo está esperando datos: al abrir, y en cada salto a un punto sin cargar. Lo
   *  dicen los eventos del propio `<video>`, que es quien sabe si tiene con qué pintar. */
  const [cargando, setCargando] = useState(false);

  const fuentes = useMemo(() => info.data?.fuentes ?? [], [info.data]);
  const fuente = fuentes.find((f) => f.clase === clase) ?? null;
  const sub = fuente?.subtitulos.find((s) => s.indice === subtitulo) ?? null;
  const incrustar = sub?.entrega === 'incrustar';
  const mime = fuente && audio !== null ? mimeDe(fuente, audio, incrustar) : null;

  // La primera fuente que haya, en el orden en que llegan: original, convertida, colocada.
  useEffect(() => {
    if (!fuentes.length || clase) return;
    const primera = fuentes[0]!;
    setClase(primera.clase);
    setAudio(seleccionInicial(primera).audio);
    setSubtitulo(null);
  }, [fuentes, clase]);

  const cambiarFuente = (c: ClaseFuente) => {
    const f = fuentes.find((x) => x.clase === c);
    if (!f) return;
    setClase(c);
    // Las pistas de la otra fuente no tienen por qué existir en esta: se vuelve a empezar.
    setAudio(seleccionInicial(f).audio);
    setSubtitulo(null);
    setError(null);
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const sesion = useRef<SesionReproduccion | null>(null);
  // Todo lo que obliga a abrir OTRO stream, en una sola clave: cambiar de fuente, de audio,
  // o poner o quitar un subtítulo incrustado. Un subtítulo de texto NO está aquí a
  // propósito: va como pista aparte y se cambia sin cortar el vídeo.
  const claveStream = fuente && audio !== null ? `${fuente.clase}|${audio}|${incrustar ? subtitulo : ''}` : null;
  const soportado = mime ? SesionReproduccion.disponible(mime) : true;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !fuente || audio === null || !mime || !claveStream || !soportado) return;

    // Se sigue por donde iba: cambiar de audio a mitad de película no tiene por qué
    // devolver al principio.
    const desde = sesion.current ? video.currentTime : 0;
    const seguia = sesion.current ? !video.paused : true;
    sesion.current?.cerrar();

    const s = new SesionReproduccion(video, {
      url: (t) =>
        urlStream(
          downloadId,
          { fuente: fuente.clase, audio, subtituloIncrustado: incrustar ? subtitulo : null },
          t,
        ),
      mime,
      duracionSegundos: fuente.duracionSegundos,
      onError: setError,
    });
    sesion.current = s;
    setError(null);
    setCargando(true);
    void s.abrir(desde);
    if (seguia) video.play().catch(() => undefined);

    return () => {
      s.cerrar();
      if (sesion.current === s) sesion.current = null;
    };
    // `fuente` cambia de identidad con cada refresco de la consulta; lo que importa de ella
    // ya está en la clave.
  }, [downloadId, claveStream, mime, soportado]);

  // «Cargando…» mientras el vídeo no tenga con qué pintar: al abrir, y tras cada salto a un
  // punto que hay que pedir al servidor. Un stream recodificándose tarda unos segundos en
  // dar el primer fragmento, y sin esto parecía que el salto no había hecho nada.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const espera = () => setCargando(true);
    const listo = () => setCargando(false);
    video.addEventListener('waiting', espera);
    video.addEventListener('seeking', espera);
    video.addEventListener('canplay', listo);
    video.addEventListener('playing', listo);
    video.addEventListener('seeked', listo);
    return () => {
      video.removeEventListener('waiting', espera);
      video.removeEventListener('seeking', espera);
      video.removeEventListener('canplay', listo);
      video.removeEventListener('playing', listo);
      video.removeEventListener('seeked', listo);
    };
  }, [fuentes.length]);

  // Un subtítulo de texto no es un `<track>`: se pide por ventanas según avanza el vídeo y
  // las entradas se añaden a una pista propia (`PistaSubtitulos`), que sobrevive a los
  // cambios de stream. Una por pista, reutilizada: volver a un subtítulo ya visto no pide
  // nada. Sin él se apagan todas.
  const subTexto = sub && sub.entrega === 'texto' ? sub : null;
  const pistas = useRef(new Map<string, PistaSubtitulos>());
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !fuente) return;
    for (const p of pistas.current.values()) p.desactivar();
    if (!subTexto) return;
    const clave = `${fuente.clase}|${subTexto.indice}`;
    let pista = pistas.current.get(clave);
    if (!pista) {
      pista = new PistaSubtitulos(video, {
        url: (ventana) => urlSubtitulo(downloadId, fuente.clase, subTexto.indice, ventana),
        duracionSegundos: fuente.duracionSegundos,
        etiqueta: rotuloSubtitulo(subTexto),
        idioma: subTexto.idioma,
        onError: setError,
      });
      pistas.current.set(clave, pista);
    }
    pista.activar();
    // `fuente` cambia de identidad con cada refresco; la clase y la pista son lo que importa.
  }, [downloadId, fuente?.clase, subTexto?.indice]);

  // Al desmontar el diálogo se va el `<video>` y con él las pistas; solo hay que cortar lo
  // que estuviera pidiéndose.
  useEffect(() => {
    const mapa = pistas.current;
    return () => {
      for (const p of mapa.values()) p.desactivar();
      mapa.clear();
    };
  }, []);

  const clases = fuentes.map((f) => f.clase);
  const errorInfo = info.error as ApiFallo | null;

  return (
    <Dialog open={abierto} onClose={onCerrar} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        Ver «{titulo}»
        <Typography variant="body2" color="text.secondary">
          Para mirar antes de decidir. No se toca ningún fichero ni cambia nada.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {info.isLoading && (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Analizando los ficheros con ffprobe…</Typography>
          </Stack>
        )}

        {errorInfo && (
          <Alert severity="error">
            {errorInfo.code === 'sin_ffmpeg'
              ? 'ffmpeg no está instalado en el servidor: sin él no hay reproductor.'
              : `No se pudo preparar la reproducción: ${errorInfo.message}`}
          </Alert>
        )}

        {info.data && fuentes.length === 0 && (
          <Alert severity="info">
            No queda ningún fichero de esta descarga en disco: ni el original de aMule, ni
            una conversión esperando, ni una copia en la biblioteca.
          </Alert>
        )}

        {fuentes.length > 0 && (
          <Stack spacing={1.5}>
            {/* Qué fichero. Con uno solo se enseña igual, para que se sepa cuál es. */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={clase}
                onChange={(_e, v: ClaseFuente | null) => v && cambiarFuente(v)}
              >
                {clases.map((c) => {
                  const d = describirClase(c);
                  return (
                    <ToggleButton key={c} value={c}>
                      <Tooltip title={d.nota}>
                        <span>{d.texto}</span>
                      </Tooltip>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
              {fuente && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                  {fuente.fileName} · {formatearTamano(fuente.sizeBytes)}
                  {fuente.duracionSegundos ? ` · ${formatearDuracion(fuente.duracionSegundos)}` : ''}
                  {fuente.video?.alto ? ` · ${fuente.video.alto}p` : ''}
                </Typography>
              )}
            </Stack>

            {fuente && !fuente.video && (
              <Alert severity="warning">Este fichero no tiene pista de vídeo: no hay nada que ver.</Alert>
            )}

            {fuente && fuente.video && !fuente.audio.length && (
              <Alert severity="warning">Este fichero no tiene ninguna pista de audio.</Alert>
            )}

            {!soportado && (
              <Alert severity="error">
                Este navegador no puede reproducir esta combinación de pistas (<code>{mime}</code>).
                Prueba con Chrome, Firefox o Safari de escritorio.
              </Alert>
            )}

            <Box sx={{ position: 'relative', bgcolor: 'black', borderRadius: 1, overflow: 'hidden' }}>
              {/* `controls` nativos: reproducir, saltar, volumen y pantalla completa vienen de
                  serie, y la barra funciona porque la sesión le dice al vídeo cuánto dura. */}
              <video
                ref={videoRef}
                controls
                playsInline
                style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', background: 'black' }}
              />
              {cargando && !error && (
                <Box
                  sx={{
                    position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', gap: 1,
                    bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 1, py: 0.5, borderRadius: 1,
                    pointerEvents: 'none',
                  }}
                >
                  <CircularProgress size={14} color="inherit" />
                  <Typography variant="caption">Cargando…</Typography>
                </Box>
              )}
            </Box>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {fuente && fuente.video && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel id={`audio-${downloadId}`}>Audio</InputLabel>
                  <Select
                    labelId={`audio-${downloadId}`}
                    label="Audio"
                    value={audio ?? ''}
                    onChange={(e) => setAudio(Number(e.target.value))}
                    disabled={!fuente.audio.length}
                  >
                    {fuente.audio.map((a) => (
                      <MenuItem key={a.indice} value={a.indice}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                          <Typography variant="body2" noWrap>{rotuloAudio(a)}</Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={a.entrega === 'directa' ? 'success' : 'default'}
                            label={a.entrega === 'directa' ? 'directo' : '→ AAC'}
                          />
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel id={`subs-${downloadId}`}>Subtítulos</InputLabel>
                  <Select
                    labelId={`subs-${downloadId}`}
                    label="Subtítulos"
                    value={subtitulo ?? -1}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setSubtitulo(v < 0 ? null : v);
                    }}
                  >
                    <MenuItem value={-1}>Sin subtítulos</MenuItem>
                    {fuente.subtitulos.map((s) => (
                      <MenuItem key={s.indice} value={s.indice}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                          <Typography variant="body2" noWrap>{rotuloSubtitulo(s)}</Typography>
                          {s.entrega === 'incrustar' && (
                            <Chip size="small" variant="outlined" color="warning" label="se incrusta" />
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )}

            {/* Qué se está copiando y qué se recodifica. Importa porque cambia lo que se
                ve: una vista previa recodificada a 720p no es la calidad del fichero, y
                juzgarla por eso sería juzgar a ffmpeg y no al release. */}
            {fuente && fuente.video && (
              <Alert severity={fuente.video.entrega === 'directa' && !incrustar ? 'success' : 'info'} icon={false}>
                <AlertTitle sx={{ fontSize: 14 }}>
                  {fuente.video.entrega === 'directa' && !incrustar
                    ? 'Vídeo sin recodificar: lo que ves es el fichero tal cual'
                    : 'Vídeo recodificado al vuelo, a 720p: la nitidez no es la del fichero'}
                </AlertTitle>
                <Typography variant="body2">
                  {incrustar
                    ? 'El subtítulo elegido es de imagen (PGS o VobSub): no hay forma de pintarlo aparte, así ' +
                      'que se incrusta en la imagen, y eso obliga a recodificar el vídeo aunque fuera H.264.'
                    : fuente.video.motivo}
                </Typography>
                {audio !== null && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Audio: {fuente.audio.find((a) => a.indice === audio)?.motivo ?? ''}
                  </Typography>
                )}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
